import moment from "moment"
import { PropsWithChildren, useEffect, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { Loading, PrintTransactionModal, TableHeader } from "components"
import { convertNumber, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById } from "utils"
import { useSubdomainSetting } from "pages/Purchase/Transaction/PurchaseOrder/utils"
import { useSupplier } from "pages/Purchase/Transaction/components/Supplier/utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { FormTransferItemsType } from "../types"
import { APPROVAL } from "pages/Sales/Register/utils/vars"

export function PrintTransferItem(params: { toggle: () => void }): JSX.Element {
  const { control } = useFormContext<FormTransferItemsType>()
  const transferDetail = useWatch({ control: control, name: "transfer_detail" })

  const transaction_date = useWatch({ control, name: "transaction_date" })
  const trx_code = useWatch({ control, name: "trx_code" })
  const transfer_detail = useWatch({ control, name: "transfer_detail" })
  const approval_status = useWatch({ control, name: "approval_status" })
  const approved_by_name = useWatch({ control, name: "approved_by_name" })

  const totalTransfer = transfer_detail.reduce((sum, item) => sum + (item.quantity_transfer ?? 0), 0)

  const setting = useSubdomainSetting()
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()

  const [address, setAddress] = useState<string>("")

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  const isDataEmpty = <tr><td colSpan={13}><Loading errorText="No data available" /></td></tr>

  return (
    <PrintTransactionModal
      isLandscape
      isLoading={company.isLoading || supplier.isLoading || country.isLoading || province.isLoading || subdistrict.isLoading || regency.isLoading}
      toggle={params.toggle}
    >
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <InfoItems uppercase label="ADDRESS" text={getAddress()} />
                <InfoItems label="PHONE" text={setting.data?.phone!} />
                <InfoItems label="EMAIL" text={setting.data?.email!} />
                <InfoItems label="WEBSITE" text={setting.data?.website!} />
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">
              TRANSFER ITEM
            </div>

            <table>
              <tbody className="font-bold ">
                <InfoItems width={280} textRight label="DATE" text={moment(transaction_date).format("DD/MM/YYYY")} />
                <InfoItems width={280} textRight label="TRANSFER ITEM NO" text={trx_code!} />
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <TableHeader>
            <tr>
              <th>#</th>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>SOH SOURCE</th>
              <th>SOH DESTINATION</th>
              <th>UNIT</th>
              <th>SHIP</th>
            </tr>
          </TableHeader>
          <tbody>
            {!transferDetail?.length ? isDataEmpty : (
              transferDetail?.map((item, key) => (
                <tr>
                  <td className="text-center px-0">{key + 1}</td>
                  <td>{item?.product_barcode}</td>
                  <td>{item?.product_name}</td>
                  <td className="text-right">{item?.soh}</td>
                  <td className="text-right">{item?.soh_destination ? convertNumber(item?.soh_destination).intoNormalAmount : ''}</td>
                  <td>{item?.mr_unit_name}</td>
                  <td className="text-right">{item?.quantity_transfer}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
  
        <section>
          <table className="table-top">
            <tbody>
              <InfoItems width={180} label="TOTAL TRANSFER" text={totalTransfer || "0"} />
              <InfoItems width={180} label="APPROVAL STATUS" text={APPROVAL[approval_status]} />
              <InfoItems width={180} label="APPROVED BY" text={approved_by_name} />
            </tbody>
          </table>
        </section>
      </section>
    </PrintTransactionModal>
  )
}

type InfoItemsType = {
  label: string
  text: string | number
  uppercase?: boolean
  textRight?: boolean
  width?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)